"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.keysToValues = keysToValues;
exports.hasChanged = hasChanged;
exports.hasChangedAny = hasChangedAny;
exports.hasChangedAll = hasChangedAll;
exports.hasChangedAllExcept = hasChangedAllExcept;
exports.buildObjectMask = buildObjectMask;
exports.getKeyByValue = getKeyByValue;
exports.deepCopy = deepCopy;
exports.omit = omit;
exports.pick = pick;
exports.toMap = toMap;
exports.valueOf = valueOf;
exports.plainToInstance = plainToInstance;
exports.instanceToPlain = instanceToPlain;
exports.Ignore = Ignore;
exports.renameKeys = renameKeys;
exports.mapByKey = mapByKey;
exports.groupById = groupById;
exports.mergeDeep = mergeDeep;
exports.hasNested = hasNested;
exports.invert = invert;
exports.removeKeysWhere = removeKeysWhere;
const class_transformer_1 = require("class-transformer");
const lodash_1 = require("lodash");
const ramda_1 = require("ramda");
const arrayChecks_1 = require("./arrayChecks");
const checks_1 = require("./checks");
/**
 * A key to value operation which preserves the order of the keys passed in
 * @param keys
 * @param obj
 * @returns
 */
function keysToValues(keys, obj) {
    return keys.map(key => obj[key]);
}
/**
 *
 * ! Arrays ignore order
 * TODO: On next major release, stop ignoring order.
 * @param key
 * @param changedObjects
 * @returns
 */
function hasChanged(key, changedObjects) {
    const { before, after } = changedObjects;
    if (!before && !after) {
        return false;
    }
    if (!before || !after) {
        return true;
    }
    const beforeValue = before[key];
    const afterValue = after[key];
    if ((0, checks_1.isArray)(beforeValue) && (0, checks_1.isArray)(afterValue)) {
        return !(0, arrayChecks_1.arrayEquals)(beforeValue, afterValue);
    }
    return !(0, checks_1.isDeepEqual)(beforeValue, afterValue);
}
/**
 * ! Arrays ignore order
 *  TODO: On next major release, stop ignoring order.
 * @param keys
 * @param changedObjects
 * @returns
 */
function hasChangedAny(keys, changedObjects) {
    return keys.some(key => hasChanged(key, changedObjects));
}
/**
 * Returns true if ANY key in the entire object has changed
 * @param changedObjects
 * @returns
 */
function hasChangedAll(changedObjects) {
    const beforeKeys = changedObjects.before ? Object.keys(changedObjects.before) : [];
    const afterKeys = changedObjects.after ? Object.keys(changedObjects.after) : [];
    const keys = (0, ramda_1.uniq)([...beforeKeys, afterKeys]);
    return hasChangedAny(keys, changedObjects);
}
/**
 * Returns true if ANY key in the entire object has changed, except for the keys passed in
 * @param keys
 * @param changedObjects
 * @returns
 */
function hasChangedAllExcept(keys, changedObjects) {
    const { before, after } = changedObjects;
    const omittedBefore = before ? omit(before, ...keys) : {};
    const omittedAfter = after ? omit(after, ...keys) : {};
    return hasChangedAll({ before: omittedBefore, after: omittedAfter });
}
function buildObjectMask(...keys) {
    const keyMap = keys.map(key => [key, undefined]);
    return keyMap.reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
}
/*
 * @param obj
 * @param value
 * @returns the first key that matches the given value
 */
function getKeyByValue(obj, value) {
    return Object.keys(obj).find(key => obj[key] === value);
}
/**
 * Creates a deep copy of the value which may contain (nested) Arrays and Objects, Numbers, Strings, Booleans and Dates.
 */
function deepCopy(obj) {
    return (0, ramda_1.clone)(obj);
}
/**
 * Omits requested properties from the object.
 * To enable static type checking, does not support paths - a new method `omitPath` should be created with looser type checking if this becomes needed functionality
 * @param obj
 * @param prop
 * @returns a new object with the requested properties omitted.
 */
function omit(obj, ...prop) {
    return (0, ramda_1.omit)(prop, obj);
}
/**
 * Picks requested properties from the object.
 * To enable static type checking, does not support paths - a new method `pickPath` should be created with looser type checking if this becomes needed functionality
 * @param obj
 * @param prop
 * @returns a new object with the requested properties picked.
 */
function pick(obj, ...prop) {
    return (0, ramda_1.pick)(prop, obj);
}
function toMap(obj) {
    if (obj instanceof Map) {
        return obj;
    }
    const m = new Map();
    for (const key in obj) {
        m.set(key, obj[key]);
    }
    return m;
}
/**
 * Returns a function that returns the specified key of T when provided
 * like @prop from ramda or @property from lodash
 * @param key
 * @returns a function which will return the value of the given key
 */
function valueOf(key) {
    return (obj) => obj[key];
}
function plainToInstance(clazz, value) {
    const m = new Map();
    for (const key in value) {
        if (value[key] instanceof Map) {
            m.set(key, value[key]);
        }
    }
    const transformed = (0, class_transformer_1.plainToInstance)(clazz, value);
    for (const [key, value] of m.entries()) {
        transformed[key] = value;
    }
    return transformed;
}
function unwrapSkipTransformContainers(obj) {
    for (const i in obj) {
        if (obj.hasOwnProperty(i)) {
            const currentValue = obj[i];
            if (currentValue?.type === '__skipTransformContainer__') {
                // retrieve the original value and also set it.
                obj[i] = currentValue();
                continue;
            }
            // recursion + recursion anchor
            if (typeof currentValue === 'object') {
                unwrapSkipTransformContainers(currentValue);
            }
        }
    }
}
function instanceToPlain(object, options) {
    const result = (0, class_transformer_1.instanceToPlain)(object, options);
    unwrapSkipTransformContainers(result);
    return result;
}
function Ignore() {
    return (0, class_transformer_1.Transform)((params) => {
        if (params.type === class_transformer_1.TransformationType.CLASS_TO_PLAIN) {
            // class-transformer won't touch functions,
            // so we use function-objects as container to skip transformation.
            const container = () => params.value;
            container.type = '__skipTransformContainer__';
            return container;
        }
        // On other transformations just return the value.
        return params.value;
    });
}
/**
 * Does not mutate the original object
 * @param obj
 * @param keys
 * @returns a new object with the keys renamed
 */
function renameKeys(obj, keys) {
    const objCopy = deepCopy(obj);
    Object.keys(keys).forEach(key => {
        const oldKey = key;
        const newKey = keys[oldKey];
        if (oldKey !== newKey) {
            objCopy[newKey] = objCopy[oldKey];
            delete objCopy[oldKey];
        }
    });
    return objCopy;
}
function mapByKey(arr, key) {
    return arr.reduce((previous, current) => {
        return previous.set(current[key], current);
    }, new Map());
}
function groupById(arr) {
    return mapByKey(arr, 'id');
}
/**
 * Like a deep Object.Assign() - merges two objects overriding the first with the second recursively
 * TODO: Enhance type safety
 * @param a
 * @param b
 * @returns
 */
function mergeDeep(a, b) {
    return (0, ramda_1.mergeDeepRight)(a, b ?? {});
}
function hasNested(item, filter) {
    for (const key of Object.keys(item)) {
        const nested = item[key];
        if (filter(nested)) {
            return true;
        }
    }
    return false;
}
/**
 * Inverts/reverses the keys/values of an object
 */
function invert(obj) {
    return (0, lodash_1.invert)(obj);
}
/**
 * Removes object keys according to the function passed in
 */
function removeKeysWhere(func, object) {
    // TODO: better type safety
    return Object.keys(object)
        .filter(k => !func(k))
        .reduce((obj, key) => ({ ...obj, [key]: object[key] }), {});
}
